import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Magnetic } from './Magnetic';
import { Link } from './nav';

const MaskedText: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => (
  <div className="overflow-hidden py-8 -my-8" style={{ transform: 'translateZ(0)' }}>
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.div>
  </div>
);

export const Hero: React.FC = () => {
  const { scrollY } = useScroll();
  const scale = useTransform(scrollY, [0, 600], [1, 0.96]);

  return (
    <motion.section
      id="home"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="relative min-h-screen pt-24 md:pt-32 bg-white flex flex-col overflow-hidden"
    >
      {/* Main Heading */}
      <div className="w-full px-2 mb-2 md:mb-6 select-none relative z-10 text-center pointer-events-none">
        <MaskedText>
          <p aria-label="Natwic Studio" className="text-[13vw] md:text-[14vw] font-black text-black leading-[0.85] tracking-[-0.07em] inline-block whitespace-nowrap">
            NatwicStudio
          </p>
        </MaskedText>
      </div>

      {/* Main Visual Container */}
      <motion.div
        style={{ scale }}
        className="relative mx-auto w-[98vw] rounded-2xl md:rounded-[3.5rem] overflow-hidden bg-black flex-1 min-h-[70vh] md:min-h-[82vh] shadow-[0_40px_100px_rgba(0,0,0,0.2)] mb-4 will-change-transform"
      >
        {/* Background */}
        <div
          className="absolute inset-0 z-0"
          style={{
            background: `
              radial-gradient(ellipse 90% 90% at -5% -5%, rgba(112,63,236,0.95) 0%, transparent 55%),
              radial-gradient(ellipse 75% 75% at 105% 55%, rgba(168,85,247,0.85) 0%, transparent 55%),
              radial-gradient(ellipse 70% 70% at 50% 110%, rgba(112,63,236,0.7) 0%, transparent 50%),
              #050505
            `
          }}
        />

        <img
          src="/media/hero-texture.webp"
          width={1600}
          height={1216}
          className="absolute inset-0 w-full h-full object-cover opacity-10 scale-110 z-0"
          alt=""
          loading="eager"
          decoding="async"
        />

        <div className="absolute inset-0 bg-black/40 z-0 pointer-events-none" />

        {/* Content Overlay */}
        <div className="absolute inset-0 z-20 flex flex-col justify-end p-6 md:p-24 pb-24 md:pb-36">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-12 md:gap-16">
            <div className="max-w-4xl pointer-events-none">
              <MaskedText delay={0.4}>
                <p className="text-[9px] md:text-xs font-bold uppercase tracking-[0.7em] mb-4 md:mb-8 text-white/50">ESTABLISHED TWENTY TWENTY FIVE</p>
              </MaskedText>
              <MaskedText delay={0.55}>
                <p className="text-3xl md:text-[4.5rem] font-bold text-white leading-[1.1] tracking-[-0.04em]">
                  Not just a studio,<br/><span className="italic text-white/40 font-medium">we are Natwic.</span>
                </p>
              </MaskedText>
              <MaskedText delay={0.7}>
                <h1 className="mt-6 md:mt-10 flex items-start gap-3 md:gap-4 max-w-2xl text-base md:text-[1.6rem] font-medium leading-[1.3] tracking-[-0.02em] text-white/50">
                  <span aria-hidden className="mt-[0.55em] w-1.5 h-1.5 md:w-2 md:h-2 shrink-0 rounded-full bg-[#703FEC] shadow-[0_0_12px_rgba(112,63,236,0.8)]" />
                  <span>
                    <span className="text-white">Web design &amp; branding</span> for startups, SaaS teams and small businesses, anywhere in the world.
                  </span>
                </h1>
              </MaskedText>
            </div>

            <Magnetic strength={0.2}>
              <Link href="/contact" className="block">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.9, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="flex items-center gap-4 md:gap-5 bg-white/5 border border-white/10 p-2 md:p-3 pr-6 md:pr-8 rounded-[1.5rem] md:rounded-[2rem] hover:bg-white/10 transition-all cursor-pointer group"
              >
                <div className="relative shrink-0">
                  <span aria-hidden className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-white/10 text-white grid place-items-center text-sm md:text-lg font-bold transition-colors duration-700 group-hover:bg-[#703FEC]">M</span>
                  <div className="absolute -top-1 -right-1 w-2.5 md:w-3.5 h-2.5 md:h-3.5 bg-[#703FEC] rounded-full border-2 border-zinc-950" />
                </div>
                <div className="text-white">
                  <p className="text-xs md:text-sm font-bold leading-none mb-1 md:mb-1.5 group-hover:text-[#703FEC] transition-colors duration-500">Contact Mark</p>
                  <p className="text-[8px] md:text-[9px] font-bold text-white/30 uppercase tracking-[0.25em]">Customer support</p>
                </div>
              </motion.div>
              </Link>
            </Magnetic>
          </div>
        </div>

        {/* Scroll Hint */}
        <div className="absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 z-40 text-center opacity-40 pointer-events-none">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="text-[8px] md:text-[10px] font-bold uppercase tracking-[0.6em] text-white flex items-center gap-4 md:gap-6"
          >
            <span className="w-8 md:w-12 h-[1px] bg-white/10" />
            SCROLL
            <span className="w-8 md:w-12 h-[1px] bg-white/10" />
          </motion.div>
        </div>
      </motion.div>
    </motion.section>
  );
};
